/*
  Récemment vus.

  Deux responsabilités séparées :
  1. mémoriser le produit consulté (sur toute fiche produit, même si le bloc n'y est pas) ;
  2. afficher les précédents là où les blocs sont posés. Il y en a deux depuis que le
     tiroir de panier en porte un : la section des fiches produit, rendue au repos du
     navigateur, et celle du tiroir, marquée `data-lazy` et rendue seulement quand on
     émet `eiken:recently-viewed:show`.

  L'historique est une liste de handles, du plus récent au plus ancien. On ne stocke ni prix
  ni image : ils seraient périmés dès la première promo. Les cartes sont demandées au thème
  au moment de l'affichage, et seulement quand le bloc approche de l'écran, pour ne pas
  peser sur le chargement initial.
*/
(function () {
  var KEY = 'eiken:recently-viewed';
  var MAX = 12;          // profondeur de l'historique conservé
  var TTL = 90 * 86400000; // 90 jours : au-delà, l'intention d'achat n'existe plus

  function read() {
    try {
      var raw = JSON.parse(localStorage.getItem(KEY) || '[]');
      if (!Array.isArray(raw)) return [];
      var now = Date.now();
      return raw.filter(function (e) {
        return e && typeof e.h === 'string' && typeof e.t === 'number' && now - e.t < TTL;
      });
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    } catch (e) {
      /* mode privé ou quota plein : la fonctionnalité se désactive, rien d'autre ne casse */
    }
  }

  function remember(handle) {
    if (!handle) return;
    var list = read().filter(function (e) { return e.h !== handle; });
    list.unshift({ h: handle, t: Date.now() });
    write(list);
  }

  // Fusionne les handles qui se sont révélés désigner le même produit (ancien handle conservé
  // par une redirection, handle d'une autre langue). On garde l'entrée la plus récente.
  function normalize(renamed) {
    var keys = [];
    for (var k in renamed) { if (renamed.hasOwnProperty(k)) keys.push(k); }
    if (!keys.length) return;
    var seen = [];
    var out = [];
    read().forEach(function (e) {
      var h = renamed[e.h] || e.h;
      if (seen.indexOf(h) !== -1) return;
      seen.push(h);
      out.push({ h: h, t: e.t });
    });
    write(out);
  }

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function cardUrl(handle) {
    return routeRoot() + 'products/' + handle + '?section_id=recently-viewed-card';
  }

  // Toutes les cartes en UNE requête (sections/recently-viewed-batch.liquid) :
  // l'ancien flux faisait un fetch PAR handle (4 à 12 requêtes par fiche
  // produit — revue 2026-09-07). Le contexte /search est le seul où Liquid
  // peut lire les handles passés (search.terms) ; aucune recherche n'est
  // exécutée. Renvoie la liste de cartes, ou null pour déclencher le repli
  // carte-par-carte (section absente, erreur réseau).
  function fetchBatch(handles) {
    var url = routeRoot() + 'search?q=' + encodeURIComponent(handles.join(' ')) +
              '&section_id=recently-viewed-batch';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return null;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var wrap = tmp.querySelector('[data-rv-batch]');
        if (!wrap) return null; // section pas (encore) déployée -> repli
        var nodes = wrap.querySelectorAll('.grid__item');
        var cards = [];
        for (var i = 0; i < nodes.length; i++) {
          // En batch, all_products ne résout que les handles ACTUELS :
          // requested == canonique par construction, les alias mémorisés
          // ne renvoient simplement pas de carte.
          var h = nodes[i].getAttribute('data-product-handle') || '';
          cards.push({ requested: h, node: nodes[i] });
        }
        return cards;
      })
      .catch(function () { return null; });
  }

  function fetchCard(handle) {
    return fetch(cardUrl(handle), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return null;
        // La Section Rendering API renvoie le wrapper de section : on ne garde que la carte.
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var item = tmp.querySelector('.grid__item') || tmp.firstElementChild;
        // On renvoie aussi le handle demandé : la carte revient sous le handle canonique,
        // la comparaison des deux révèle les alias mémorisés.
        return item ? { requested: handle, node: item } : null;
      })
      .catch(function () { return null; });
  }

  function render(root) {
    var grid = root.querySelector('[data-recently-viewed-grid]');
    if (!grid) return;

    var currentId = root.dataset.currentId || '';
    var limit = parseInt(root.dataset.limit, 10) || 4;
    var queue = read().map(function (e) { return e.h; }).filter(Boolean);

    if (!queue.length) return;

    var kept = [];    // cartes retenues, dans l'ordre de l'historique
    var seen = [];    // ids produit déjà retenus
    var renamed = {}; // handle mémorisé -> handle canonique

    // Le dédoublonnage se fait sur l'ID PRODUIT, jamais sur le handle : un même produit peut
    // être mémorisé sous deux handles (ancien handle après un renommage, handle d'une autre
    // langue), Shopify redirige les deux vers la même fiche et renvoie deux cartes identiques.
    // Même raison pour exclure le produit courant par son id et non par son handle.
    function keep(cards) {
      cards.forEach(function (card) {
        // Un produit dépublié depuis sa consultation ne renvoie pas de carte : on l'ignore
        // silencieusement plutôt que d'afficher un trou dans la grille.
        if (!card || kept.length >= limit) return;
        var id = card.node.getAttribute('data-product-id');
        var canonical = card.node.getAttribute('data-product-handle');
        if (canonical && card.requested && canonical !== card.requested) renamed[card.requested] = canonical;
        if (!id || id === currentId || seen.indexOf(id) !== -1) return;
        seen.push(id);
        kept.push(card.node);
      });
    }

    // Repli carte-par-carte (une requête par handle) : sert uniquement si la
    // section batch est absente ou en erreur. C'est aussi le seul chemin qui
    // détecte les alias de handle (le batch ne résout que les handles actuels).
    function fill() {
      var need = limit - kept.length;
      if (need <= 0 || !queue.length) return Promise.resolve();
      return Promise.all(queue.splice(0, need).map(fetchCard)).then(function (cards) {
        keep(cards);
        return fill(); // les places libérées par un doublon sont reprises par la suite de la liste
      });
    }

    function finish() {
      normalize(renamed);
      if (!kept.length) return;
      kept.forEach(function (n) { grid.appendChild(n); });
      root.removeAttribute('hidden');
      document.dispatchEvent(new CustomEvent('eiken:recently-viewed:rendered'));
    }

    fetchBatch(queue.slice(0, 16)).then(function (cards) {
      if (cards) {
        keep(cards);
        finish();
        return;
      }
      return fill().then(finish);
    });
  }

  function init() {
    // 1. mémoriser, sur toute fiche produit.
    //    On lit l'URL et NON `[data-product-handle]` : cet attribut est aussi porté par chaque
    //    carte de grille, donc sur une page collection il aurait fait enregistrer le premier
    //    produit affiché comme s'il avait été consulté.
    //    Et on prend le handle de la balise canonique quand elle existe : une fiche atteinte
    //    par un ancien handle garde cet ancien handle dans l'URL, la canonique porte le bon.
    //    Jamais sur la page 404 (2026-10-04) : son adresse peut être /products/<x> alors que
    //    le produit n'existe pas, ou pas dans ce marché ; il serait mémorisé comme consulté.
    if (location.pathname.match(/\/products\/([^/?#]+)/) && !document.querySelector('[data-e404]')) {
      var canonical = document.querySelector('link[rel="canonical"]');
      var source = (canonical && canonical.href) || location.pathname;
      var m = source.match(/\/products\/([^/?#]+)/) || location.pathname.match(/\/products\/([^/?#]+)/);
      if (m) remember(decodeURIComponent(m[1]));
    }

    // 2. afficher, là où LES blocs existent.
    //
    //    `querySelectorAll` et non `querySelector` : depuis que le tiroir de panier porte
    //    lui aussi un bloc, il y a deux hôtes sur une fiche produit. Au singulier, le
    //    premier du DOM (le tiroir, présent sur toutes les pages) volait le rendu à la
    //    section de la fiche, qui restait vide sans la moindre erreur.
    var hotes = document.querySelectorAll('[data-recently-viewed]');
    if (!hotes.length) return;

    // Surtout PAS d'IntersectionObserver ici : le conteneur porte `hidden` tant qu'il n'a rien
    // à montrer, donc `display:none`, donc aucune boîte de rendu et aucun croisement possible.
    // L'observateur n'aurait jamais déclenché, et c'est lui qui devait retirer le `hidden`.
    // On diffère donc sur le temps de repos du navigateur, qui ne dépend d'aucune géométrie.
    function planifier(root) {
      if (root.dataset.rendu) return;   // une seule fois par hôte
      root.dataset.rendu = '1';
      if ('requestIdleCallback' in window) {
        requestIdleCallback(function () { render(root); }, { timeout: 2000 });
      } else {
        setTimeout(function () { render(root); }, 1200);
      }
    }

    // Un hôte marqué `data-lazy` n'est rendu que sur demande. C'est le cas du bloc posé
    // dans le tiroir : il est présent sur TOUTES les pages du site, et le rendre au
    // chargement déclencherait une requête de carte produit par article d'historique, sur
    // chaque page vue, pour un bloc que la plupart des visiteurs n'ouvriront jamais.
    var differes = [];
    for (var i = 0; i < hotes.length; i++) {
      if (hotes[i].hasAttribute('data-lazy')) differes.push(hotes[i]);
      else planifier(hotes[i]);
    }

    if (differes.length) {
      document.addEventListener('eiken:recently-viewed:show', function () {
        for (var j = 0; j < differes.length; j++) planifier(differes[j]);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
